import { NavArrowRight as CaretRightIcon } from 'iconoir-react'
import classNames from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { ControlledSearch } from 'app/components/ui/controlled-search'
import { TableFiltersView as TableFilters } from 'app/components/ui/table-filters/table-filters'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'app/components/ui/table'
import { DateComponent } from 'app/components/DateComponent'
import { LazyWorkspaceCell } from 'app/components/LazyWorkspaceCell'
import { IntegrationIcon } from 'app/table-columns/integrations'
import { fetchRowResolvedStatus } from './integrationStatus'
import { SyncProgressBar } from './SyncProgressBar'
import type { IntegrationBadgeStatus } from './statusModel'
import { bg } from 'lib/background'
import type { FilterableColumn } from 'app/filter.utils'
import type { IntegrationsRow, SyncProgressByLocation } from 'app/types/integrations'
import { Button } from '../ui/button'

// `Loading` is a transient bucket for rows whose async status hasn't resolved
// yet; `Unknown` catches rows whose status endpoint returned nothing.
type GroupKey = IntegrationBadgeStatus | `Unknown` | `Loading`

// Group ordering + marker-dot colors, mirroring the Tasks grouped-table status
// treatment (a colored dot beside a semibold label).
const GROUP_ORDER: GroupKey[] = [`Active`, `In Progress`, `Broken`, `Deleting`, `Unknown`, `Loading`]
const GROUP_DOT: Record<GroupKey, string> = {
  Active: `bg-green-500`,
  'In Progress': `bg-yellow-500`,
  Broken: `bg-red-500`,
  Deleting: `bg-(--fg-minimal)`,
  Unknown: `bg-(--line-strong)`,
  Loading: `bg-surface-strong`,
}

export type IntegrationsLocationsGroupedComponentProps = {
  rows: IntegrationsRow[]
  filters: FilterableColumn[]
  setSearchValue: (val: string) => void
  onIntegrationClick: (row: IntegrationsRow) => void
  /** True while the underlying row list itself is still being fetched. */
  isLoading: boolean
  /** Bumped after an action invalidates cached statuses, forcing a re-resolve. */
  statusEpoch: number
  syncProgress: SyncProgressByLocation
}

// ─── Column layout ───────────────────────────────────────────────────────────
// Single source of truth for the columns: drives both the shared <colgroup>
// (width) and the sticky header (label), so the header table and each group's
// table stay aligned. Mirrors the TasksTable grouped layout.

const COLUMNS = [
  { width: `w-[220px]`, label: `Integration` },
  { width: `w-[220px]`, label: `Location` },
  { width: `w-[220px]`, label: `Workspace` },
  // Width-less: with `table-fixed` + `w-full`, Integration ID absorbs whatever
  // width remains, so the table always fills the page.
  { width: undefined, label: `Integration ID` },
  // Sized for the longest nowrap date ("Yesterday at 12:40 AM" ≈ 150px) plus
  // the px-5 cell padding — the date overflows the cell (and clips at the card
  // edge) if this is too narrow.
  { width: `w-[190px]`, label: `Last Update` },
] as const

// Floor for the tables and their card wrappers: the fixed columns (850px) plus
// a readable minimum for the flexible Integration ID column. Below this the
// groups container pans horizontally instead of crushing the ID column.
const TABLE_MIN_W = `min-w-[1100px]`

// Shared cell class for the wrapping text columns (Location / Workspace / ID).
const WRAP_CELL = `whitespace-normal wrap-break-word px-5 py-3.5 text-body-sm`

function LocationsTableColGroup() {
  return (
    <colgroup>
      {COLUMNS.map((c) => (
        <col key={c.label} className={c.width} />
      ))}
    </colgroup>
  )
}

function LocationsHeaderCells() {
  return (
    <TableRow className="bg-surface-subtle hover:bg-surface-subtle">
      {COLUMNS.map((c) => (
        <TableHead key={c.label} className="px-5 text-ui-xs font-bold text-default">
          {c.label}
        </TableHead>
      ))}
    </TableRow>
  )
}

// Shared table shell (colgroup + fixed layout) used by both the sticky header
// table and each group's body table so their columns line up.
function LocationsTable({ children }: { children: ReactNode }) {
  return (
    <table className={`w-full caption-bottom text-sm table-fixed ${TABLE_MIN_W}`}>
      <LocationsTableColGroup />
      {children}
    </table>
  )
}

function IntegrationCell({ row, onClick }: { row: IntegrationsRow; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-x-1 text-left outline-none hover:opacity-70"
    >
      <IntegrationIcon integration={row.integration} />
      <span className="text-ui-xs">{row.integration}</span>
    </button>
  )
}

function StatusGroup({
  status,
  rows,
  onIntegrationClick,
  syncProgress,
}: {
  status: GroupKey
  rows: IntegrationsRow[]
  onIntegrationClick: (row: IntegrationsRow) => void
  syncProgress: SyncProgressByLocation
}) {
  const [open, setOpen] = useState(true)

  return (
    <div className="flex flex-col">
      <Button
        onClick={() => setOpen((v) => !v)}
        variant="icon"
        size="inline"
        className="flex h-9 w-full items-center justify-start"
      >
        <CaretRightIcon
          className={classNames(`h-3.5 w-3.5 text-subtle transition-transform`, open && `rotate-90`)}
          strokeWidth={2.5}
        />
        <span className={classNames(`h-2 w-2 shrink-0 rounded-full`, GROUP_DOT[status])} aria-hidden />
        <span className="text-body-sm font-semibold tracking-tight text-strong">{status}</span>
        <span className="text-label-xs leading-none font-medium tabular-nums text-minimal">{rows.length}</span>
      </Button>
      {open && (
        <div className="overflow-hidden rounded-md border border-default bg-surface shadow-elevation-1">
          <LocationsTable>
            <TableBody
              className="divide-y divide-subtle
            "
            >
              {rows.map((row) => {
                const runningSync = syncProgress[row.locationId]?.[row.integration]
                return (
                  <TableRow key={row.key}>
                    <TableCell className="px-5 py-3.5">
                      <IntegrationCell row={row} onClick={() => onIntegrationClick(row)} />
                    </TableCell>
                    <TableCell className={`${WRAP_CELL} text-strong`}>{row.locationName}</TableCell>
                    <TableCell className={`${WRAP_CELL} text-default`}>
                      <LazyWorkspaceCell workspaceId={row.workspaceId} />
                    </TableCell>
                    <TableCell className={`${WRAP_CELL} text-default`}>{row.ids?.join(`, `)}</TableCell>
                    <TableCell className="px-5 py-3.5 text-body-sm text-default">
                      {runningSync ? (
                        <SyncProgressBar job={runningSync} />
                      ) : (
                        <DateComponent value={row.lastUpdate} useLocalUtc />
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </LocationsTable>
        </div>
      )}
    </div>
  )
}

export function IntegrationsLocationsGroupedComponent({
  rows,
  filters,
  setSearchValue,
  onIntegrationClick,
  isLoading,
  statusEpoch,
  syncProgress,
}: IntegrationsLocationsGroupedComponentProps) {
  // Resolved status per row key. Status is NOT on the list payload — each row's
  // status comes from a per-integration async endpoint (same one the status
  // cell uses, shared via `integrationStatus`), so we resolve them here and
  // group once they land. Rows sit in the "Loading" group until resolved.
  const [statusByKey, setStatusByKey] = useState<Record<string, GroupKey>>({})
  const [resolvedOnce, setResolvedOnce] = useState(false)

  // `useClientSideTableFilters` hands back a fresh array on every pass, so key
  // the resolver on the stable, sorted list of row keys rather than the array
  // identity — otherwise the effect re-fires in a loop and never settles.
  const rowKeysSignature = useMemo(
    () =>
      rows
        .map((r) => r.key)
        .sort()
        .join(`|`),
    [rows],
  )

  useEffect(() => {
    let cancelled = false
    // Resolve every row concurrently (fired in the same pass, not chained),
    // but write each row's status into state as soon as ITS request lands
    // rather than waiting for the slowest one -- with one ES-heavy request
    // per row, a single straggler in a large org shouldn't hold the whole
    // table on the skeleton. `resolvedOnce` flips on the first row to settle
    // (an empty `rows` batch never settles anything, so it never flips).
    bg(
      Promise.all(
        rows.map(async (row) => {
          const resolved = await fetchRowResolvedStatus(row).catch(() => undefined)
          if (cancelled) return
          setStatusByKey((prev) => ({ ...prev, [row.key]: resolved?.status ?? `Unknown` }))
          setResolvedOnce(true)
        }),
      ),
    )
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowKeysSignature, statusEpoch])

  const groups = useMemo(() => {
    const byStatus = new Map<GroupKey, IntegrationsRow[]>()
    for (const row of rows) {
      const key = statusByKey[row.key] ?? `Loading`
      const bucket = byStatus.get(key)
      if (bucket) bucket.push(row)
      else byStatus.set(key, [row])
    }
    return GROUP_ORDER.filter((s) => byStatus.has(s)).map((s) => ({ status: s, rows: byStatus.get(s)! }))
  }, [rows, statusByKey])

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mb-4 flex h-10 shrink-0 items-center gap-2 bg-surface">
        <ControlledSearch onValueChange={setSearchValue} />
        <TableFilters columns={filters} />
      </div>
      {(isLoading && rows.length === 0) || (!resolvedOnce && rows.length > 0) ? (
        <div className="flex flex-col gap-4" role="status" aria-busy="true">
          <span className="sr-only">Loading integration statuses</span>
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-md border border-default bg-surface-subtle" />
          ))}
        </div>
      ) : groups.length === 0 ? (
        <div className="py-5 text-center text-subtle">No data found</div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-auto">
          {/* `TABLE_MIN_W` here keeps the card wrappers as wide as their tables
              when the viewport is narrower than the table floor, so the rounded
              borders keep enclosing the tables while the container pans. */}
          <div className={`flex shrink-0 flex-col gap-5 pb-3 ${TABLE_MIN_W}`}>
            {/* Sticky column header table */}
            <div className="sticky top-0 z-10 w-full overflow-hidden rounded-md border border-default bg-surface">
              <LocationsTable>
                <TableHeader>
                  <LocationsHeaderCells />
                </TableHeader>
              </LocationsTable>
            </div>
            {groups.map((g) => (
              <StatusGroup
                key={g.status}
                status={g.status}
                rows={g.rows}
                onIntegrationClick={onIntegrationClick}
                syncProgress={syncProgress}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
